import { autorun, makeAutoObservable, reaction, toJS } from "mobx";
import { ClientTargetingEvaluator } from "@common/targeting/ClientGuideTargetingEvaluator";
import { pageInfo } from "./PageInfo";
import { Selector, SelectorObserver } from "./SelectorObserver";
import { PersistentStorage } from "@/lib/tour/persistent-storage/storage";
import { GuideRegistry } from "./GuideRegistry";
import { ChecklistClientMatchEvaluation, GuideClientMatchEvaluation } from "@common/targeting/audience-check";
import { ChecklistRegistry } from "./ChecklistRegistry";

interface SyncManager {
  isSettled: boolean;
}

export interface TargetingEvaluatorParams {
  syncManager: SyncManager;
  storage: PersistentStorage;
  guideRegistry: GuideRegistry;
  checklistRegistry: ChecklistRegistry;
}

export type GuideEvaluationResultWithOrder = {
  guideId: string;
  details: GuideClientMatchEvaluation;
  matchOrder: number;
};

export type ChecklistEvaluationResultWithOrder = {
  checklistId: string;
  details: ChecklistClientMatchEvaluation;
  matchOrder: number;
};

// This class is used to pick a guide based on the targeting conditions.
// It checks for elements in a page and url
export class TargetingEvaluator {
  selectorObserver = new SelectorObserver();
  evaluatedGuides: GuideEvaluationResultWithOrder[] = [];
  evaluatedChecklists: ChecklistEvaluationResultWithOrder[] = [];
  storage: PersistentStorage;
  page = pageInfo;
  syncManager: SyncManager;
  guideRegistry: GuideRegistry;
  checklistRegistry: ChecklistRegistry;

  constructor({ syncManager, guideRegistry, checklistRegistry, storage }: TargetingEvaluatorParams) {
    makeAutoObservable(this);

    this.syncManager = syncManager;
    this.storage = storage;
    this.guideRegistry = guideRegistry;
    this.checklistRegistry = checklistRegistry;

    autorun(() => {
      // Update selectorObserver whenever the selectors change
      this.selectorObserver.update(this.selectors);
    });

    reaction(
      () => [this.selectorObserver.foundSelectors, this.prefilteredGuides, this.prefilteredChecklists, this.page.url],
      () => {
        this.evaluateGuidesAndChecklists();
      }
    );
  }

  get allGuides() {
    if (!this.syncManager.isSettled || !this.storage.userState.isHydrated) return [];

    const dismissedGuideIds = this.storage.userState.dismissedTourGuideIds;
    const finishedGuideIds = this.storage.userState.finishedTourGuideIds;
    const expiredGuideIds = this.storage.sessionState.expiredTourGuideIds;
    return this.guideRegistry.guides.map((guide) => {
      const prefilterResult = ClientTargetingEvaluator.prefilterGuide({
        guide,
        dismissedGuideIds,
        finishedGuideIds,
        expiredGuideIds,
      });

      return {
        id: guide.id,
        targeting: guide.targeting,
        settings: guide.settings,
        matchEvaulation: {
          ...guide.matchEvaulation,
          prefilter: prefilterResult,
          result: false,
        },
      };
    });
  }

  get allChecklists() {
    if (!this.syncManager.isSettled || !this.storage.userState.isHydrated) return [];

    return this.checklistRegistry.checklists.map((checklist) => {
      const prefilterResult = ClientTargetingEvaluator.prefilterChecklist({
        checklist,
        dismissedChecklistIds: this.storage.userState.dismissedChecklistIds,
        finishedGuideIds: this.storage.userState.finishedTourGuideIds,
      });

      return {
        ...checklist,
        matchEvaluation: {
          ...checklist.matchEvaluation,
          message: prefilterResult.message,
          prefilter: prefilterResult,
          result: false,
        },
      };
    });
  }

  get prefilteredGuides() {
    return this.allGuides.filter((guide) => guide.matchEvaulation.prefilter.result);
  }

  get prefilteredChecklists() {
    return this.allChecklists.filter((checklist) => checklist.matchEvaluation.prefilter.result);
  }

  get selectors() {
    const selectors: Selector[] = [];
    for (const guide of this.prefilteredGuides) {
      guide.targeting?.where?.forEach((condition) => {
        if (condition.left_operand === "selector") {
          selectors.push({ target: condition.right_operand });
        }
      });
    }

    return selectors;
  }

  evaluationIndex = 0;

  async evaluateGuidesAndChecklists() {
    if (!this.syncManager.isSettled) return [];
    const currentEvaluationIndex = ++this.evaluationIndex;

    const evaluator = new ClientTargetingEvaluator({
      url: this.page.url,
      selectorObserver: this.selectorObserver,
      localTime: new Date(),
      firstTimeVisitedAt: new Date(this.storage.sessionState.getVisitedAt() as number),
    });

    this.evaluatedGuides = this.allGuides.map(this.buildEvaluatingGuide);
    this.evaluatedChecklists = this.allChecklists.map(this.buildEvaluatingChecklist);

    let guideMatchOrder = 1;

    const guideEvaluations = this.allGuides.map(async (guide, index) => {
      const matchEvaulation = await evaluator.evaluateGuide(guide);
      const order = matchEvaulation.result ? guideMatchOrder++ : -1;

      const evaluatedGuide: GuideEvaluationResultWithOrder = {
        guideId: guide.id,
        details: matchEvaulation,
        matchOrder: order,
      };

      // Most of the time we don't receive user id initially and dismissed/finished guides will be empty if local storage is cleared
      // Whenever client calls identify with user id, guides are re-evaluated with dismissed/finished guides that restored from server
      // Following check prevents evaluation results from being stored if more recent evaluation is in progress
      // Otherwise, dismissed/finished guides will be started immediately without waiting for recent evaluation to finish
      const isMostRecentEvaluation = this.evaluationIndex === currentEvaluationIndex;
      if (isMostRecentEvaluation) {
        this.evaluatedGuides[index] = evaluatedGuide;
      }
    });

    let checklistMatchOrder = 1;

    const checklistEvaluations = this.allChecklists.map(async (checklist, index) => {
      const matchEvaluation = await evaluator.evaluateChecklist(checklist);
      const order = matchEvaluation.result ? checklistMatchOrder++ : -1;

      const evaluatedChecklist: ChecklistEvaluationResultWithOrder = {
        checklistId: checklist.id,
        details: matchEvaluation,
        matchOrder: order,
      };

      const isMostRecentEvaluation = this.evaluationIndex === currentEvaluationIndex;
      if (isMostRecentEvaluation) {
        this.evaluatedChecklists[index] = evaluatedChecklist;
      }
    });

    await Promise.allSettled([...guideEvaluations, ...checklistEvaluations]);
  }

  buildEvaluatingGuide(guide: {
    id: string;
    targeting: { isEnabled?: boolean };
    matchEvaulation: GuideClientMatchEvaluation;
  }) {
    return {
      guideId: guide.id,
      details: {
        result: false,
        message: "CLIENT_EVALUATING",
        isGenerated: guide.matchEvaulation.isGenerated,
        isPublished: guide.matchEvaulation.isPublished,
        isWhoTargeted: guide.matchEvaulation.isWhoTargeted,
        prefilter: guide.matchEvaulation.prefilter,
        isTargetingEnabled: guide.targeting.isEnabled,
      },
      matchOrder: -1,
    };
  }

  buildEvaluatingChecklist(checklist: {
    id: string;
    targeting: { isEnabled?: boolean };
    matchEvaluation: ChecklistClientMatchEvaluation;
  }) {
    return {
      checklistId: checklist.id,
      details: {
        result: false,
        message: "CLIENT_EVALUATING",
        isPublished: checklist.matchEvaluation.isPublished,
        isWhoTargeted: checklist.matchEvaluation.isWhoTargeted,
        prefilter: checklist.matchEvaluation.prefilter,
        isTargetingEnabled: checklist.targeting.isEnabled,
      },
      matchOrder: -1,
    };
  }

  get firstMatchingGuideId() {
    return (
      this.evaluatedGuides.find(
        (guide) => guide.details.result && guide.details.isPublished && guide.details.isTargetingEnabled
      )?.guideId ?? null
    );
  }

  get firstMatchingChecklistId() {
    return (
      this.evaluatedChecklists.find(
        (checklist) => checklist.details.result && checklist.details.isPublished && checklist.details.isTargetingEnabled
      )?.checklistId ?? null
    );
  }

  findGuideEvaluationResultById(id: string) {
    return this.evaluatedGuides.find(({ guideId }) => guideId === id);
  }

  findChecklistEvaluationResultById(id: string) {
    return this.evaluatedChecklists.find(({ checklistId }) => checklistId === id);
  }
}
